import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { RegisterFormData, siteSettingsApi } from '../services/api';
import PasswordStrengthIndicator from '../components/PasswordStrengthIndicator';
import { validatePasswordStrength } from '../utils/passwordValidation';
import toast from 'react-hot-toast';

interface OTPForm {
  otpCode: string;
}

const RegisterPage: React.FC = () => {
  const { register: registerUser, verifyOtp, isAuthenticated, isAdminAuthenticated, isInitialized } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get phone number from navigation state if redirected from login
  const prefilledPhoneNumber = location.state?.phoneNumber || '';
  
  // Step state: 'registration' or 'otp'
  const [step, setStep] = useState<'registration' | 'otp'>('registration');
  const [registrationData, setRegistrationData] = useState<RegisterFormData | null>(null);

  // Fetch public site settings to determine if password is required
  const { data: siteSettings } = useQuery({
    queryKey: ['publicSiteSettings'],
    queryFn: async () => {
      const response = await siteSettingsApi.getPublic();
      return response.data;
    },
  });

  // Redirect authenticated users away from registration page
  useEffect(() => {
    if (isInitialized) {
      if (isAdminAuthenticated) {
        navigate('/admin/dashboard', { replace: true });
      } else if (isAuthenticated) {
        navigate('/', { replace: true });
      }
    }
  }, [isInitialized, isAuthenticated, isAdminAuthenticated, navigate]);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting }
  } = useForm<RegisterFormData>({
    defaultValues: {
      userType: 'Individual',
      phoneNumber: prefilledPhoneNumber
    }
  });

  const {
    register: registerOTP,
    handleSubmit: handleSubmitOTP,
    formState: { errors: otpErrors, isSubmitting: isSubmittingOTP }
  } = useForm<OTPForm>();

  const userType = watch('userType');
  const password = watch('password');
  const isOtpOnlyMode = siteSettings?.enableOtpOnlyLogin ?? true;

  // Don't show registration page if user is authenticated
  if (!isInitialized || isAuthenticated || isAdminAuthenticated) {
    return null;
  }

  const onSubmit = async (data: RegisterFormData) => {
    if (isSubmitting) return; // Prevent duplicate submissions
    
    try {
      // Clean up empty businessWebsite values to prevent validation errors
      const cleanedData = {
        ...data,
        businessWebsite: data.businessWebsite?.trim() || undefined
      };
      
      await registerUser(cleanedData);
      setRegistrationData(cleanedData);
      
      if (isOtpOnlyMode) {
        setStep('otp');
        toast.success('کد تأیید به شماره تلفن شما ارسال شد!');
      } else {
        // For password-based registration, show success message and redirect
        toast.success('ثبت‌نام با موفقیت انجام شد! لطفاً ایمیل خود را برای لینک فعال‌سازی بررسی کنید.');
        navigate('/login');
      }
    } catch (error: any) {
      // Handle ModelState validation errors (field-specific errors)
      const errorData = error.response?.data;
      let errorMessage = 'ثبت‌نام با خطا مواجه شد';
      
      if (errorData) {
        // Check if it's a simple message error
        if (errorData.message) {
          errorMessage = errorData.message;
        } 
        // Check if it's ASP.NET Core ModelState format with 'errors' property
        else if (errorData.errors && typeof errorData.errors === 'object') {
          // Extract first validation error from ModelState errors object
          const firstFieldErrors = Object.values(errorData.errors).find((errors: any) => 
            Array.isArray(errors) && errors.length > 0
          ) as string[] | undefined;
          
          if (firstFieldErrors && firstFieldErrors.length > 0) {
            errorMessage = firstFieldErrors[0];
          }
        }
        // Fallback: Check if errorData itself is a ModelState object (direct field errors)
        else if (typeof errorData === 'object' && !Array.isArray(errorData) && !errorData.errors) {
          // Extract first validation error from ModelState
          const firstFieldError = Object.values(errorData).find((errors: any) => 
            Array.isArray(errors) && errors.length > 0
          ) as string[] | undefined;
          
          if (firstFieldError && firstFieldError.length > 0) {
            errorMessage = firstFieldError[0];
          }
        }
      }
      
      toast.error(errorMessage);
    }
  };

  const onSubmitOTP = async (data: OTPForm) => {
    if (!registrationData) return;
    
    try {
      const verificationTarget = isOtpOnlyMode ? registrationData.phoneNumber : registrationData.email;
      await verifyOtp(verificationTarget, data.otpCode);
      toast.success('حساب کاربری با موفقیت تأیید شد! خوش آمدید!');
      navigate('/');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'کد تأیید نامعتبر است');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            {step === 'registration' ? 'ایجاد حساب کاربری' : 'تأیید شماره تلفن'}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            {step === 'registration' ? (
              <>
                یا{' '}
                <Link
                  to="/login"
                  className="font-medium text-primary-600 hover:text-primary-500"
                >
                  ورود به حساب موجود
                </Link>
              </>
            ) : (
              <>
                کد تأیید ارسال شده به{' '}
                <span className="font-medium text-gray-900">{registrationData?.phoneNumber}</span>
              </>
            )}
          </p>
          {prefilledPhoneNumber && step === 'registration' && (
            <div className="mt-4 p-3 bg-primary-50 border border-primary-200 rounded-md">
              <p className="text-sm text-primary-700 text-center">
                📱 شماره تلفن شما از قبل پر شده است. ادامه دهید تا ثبت‌نام خود را تکمیل کنید.
              </p>
            </div>
          )}
          {step === 'otp' && (
            <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-md">
              <p className="text-sm text-green-700 text-center">
                📝 اطلاعات شما ثبت شده است. لطفاً کد تأیید را وارد کنید تا حساب کاربری شما فعال شود.
              </p>
            </div>
          )}
        </div>

        {step === 'registration' ? (
        <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
          {/* Account Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              نوع حساب کاربری
            </label>
            <div className="grid grid-cols-2 gap-4">
              <label className="relative">
                <input
                  {...register('userType')}
                  type="radio"
                  value="Individual"
                  className="sr-only"
                />
                <div className={`p-4 border rounded-lg cursor-pointer text-center ${
                  userType === 'Individual' 
                    ? 'border-primary-500 bg-primary-50 text-primary-700' 
                    : 'border-gray-300 bg-white text-gray-700'
                }`}>
                  <div className="text-2xl mb-2">👤</div>
                  <div className="font-medium">شخصی</div>
                  <div className="text-sm">حساب فردی</div>
                </div>
              </label>
              <label className="relative">
                <input
                  {...register('userType')}
                  type="radio"
                  value="Business"
                  className="sr-only"
                />
                <div className={`p-4 border rounded-lg cursor-pointer text-center ${
                  userType === 'Business' 
                    ? 'border-primary-500 bg-primary-50 text-primary-700' 
                    : 'border-gray-300 bg-white text-gray-700'
                }`}>
                  <div className="text-2xl mb-2">🏢</div>
                  <div className="font-medium">تجاری</div>
                  <div className="text-sm">حساب شرکت</div>
                </div>
              </label>
            </div>
          </div>

          {/* Personal Information */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                نام
              </label>
              <input
                {...register('firstName', { required: 'نام الزامی است' })}
                type="text"
                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                placeholder="علی"
              />
              {errors.firstName && (
                <p className="mt-1 text-sm text-red-600">{errors.firstName.message}</p>
              )}
            </div>
            <div>
              <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                نام خانوادگی
              </label>
              <input
                {...register('lastName', { required: 'نام خانوادگی الزامی است' })}
                type="text"
                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                placeholder="احمدی"
              />
              {errors.lastName && (
                <p className="mt-1 text-sm text-red-600">{errors.lastName.message}</p>
              )}
            </div>
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
              آدرس ایمیل
            </label>
            <input
              {...register('email', {
                required: 'ایمیل الزامی است',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'آدرس ایمیل نامعتبر است'
                }
              })}
              type="email"
              className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              placeholder="ali@example.com"
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700">
              شماره تلفن
            </label>
            <input
              {...register('phoneNumber', {
                required: 'شماره تلفن الزامی است',
                pattern: {
                  value: /^[\+]?[0-9]{10,15}$/,
                  message: 'لطفاً شماره تلفن معتبر وارد کنید'
                }
              })}
              type="tel"
              className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              placeholder="09123456789"
            />
            {errors.phoneNumber && (
              <p className="mt-1 text-sm text-red-600">{errors.phoneNumber.message}</p>
            )}
          </div>

          {/* Password Fields - Only show when OTP-only login is disabled */}
          {!isOtpOnlyMode && (
            <>
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  رمز عبور
                </label>
                <input
                  {...register('password', {
                    required: !isOtpOnlyMode ? 'رمز عبور الزامی است' : false,
                    validate: !isOtpOnlyMode ? (value) => {
                      if (!value) return true;
                      return validatePasswordStrength(value) || 'رمز عبور شرایط قدرت را برآورده نمی‌کند';
                    } : undefined
                  })}
                  type="password"
                  className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  placeholder="یک رمز عبور قوی وارد کنید"
                />
                {errors.password && (
                  <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
                )}
                <PasswordStrengthIndicator password={password || ''} />
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                  تأیید رمز عبور
                </label>
                <input
                  {...register('confirmPassword', {
                    required: !isOtpOnlyMode ? 'لطفاً رمز عبور خود را تأیید کنید' : false,
                    validate: !isOtpOnlyMode ? (value) => {
                      if (!password && !value) return true;
                      return value === password || 'رمزهای عبور مطابقت ندارند';
                    } : undefined
                  })}
                  type="password"
                  className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  placeholder="Confirm your password"
                />
                {errors.confirmPassword && (
                  <p className="mt-1 text-sm text-red-600">{errors.confirmPassword.message}</p>
                )}
              </div>
            </>
          )}

          {/* Business Information */}
          {userType === 'Business' && (
            <>
              <div>
                <label htmlFor="businessName" className="block text-sm font-medium text-gray-700">
                  نام کسب‌وکار
                </label>
                <input
                  {...register('businessName', {
                    required: userType === 'Business' ? 'نام کسب‌وکار الزامی است' : false
                  })}
                  type="text"
                  className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  placeholder="نام شرکت شما"
                />
                {errors.businessName && (
                  <p className="mt-1 text-sm text-red-600">{errors.businessName.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="businessLicense" className="block text-sm font-medium text-gray-700">
                  شماره مجوز کسب‌وکار (اختیاری)
                </label>
                <input
                  {...register('businessLicense')}
                  type="text"
                  className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  placeholder="شماره مجوز رسمی کسب‌وکار"
                />
                {errors.businessLicense && (
                  <p className="mt-1 text-sm text-red-600">{errors.businessLicense.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="businessDescription" className="block text-sm font-medium text-gray-700">
                  توضیحات کسب‌وکار
                </label>
                <textarea
                  {...register('businessDescription')}
                  rows={3}
                  className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  placeholder="کسب‌وکار خود را توصیف کنید..."
                />
              </div>

              <div>
                <label htmlFor="businessWebsite" className="block text-sm font-medium text-gray-700">
                  وب‌سایت کسب‌وکار (اختیاری)
                </label>
                <input
                  {...register('businessWebsite', {
                    validate: (value) => {
                      if (!value || value.trim() === '') return true; // Allow empty values
                      try {
                        new URL(value);
                        return true;
                      } catch {
                        return 'لطفاً آدرس وب‌سایت معتبر وارد کنید (مثال: https://example.com)';
                      }
                    }
                  })}
                  type="url"
                  className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  placeholder="https://example.com"
                />
                {errors.businessWebsite && (
                  <p className="mt-1 text-sm text-red-600">{errors.businessWebsite.message}</p>
                )}
              </div>
            </>
          )}

          <div>
            <button
              type="submit"
              disabled={isSubmitting}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'در حال ایجاد حساب...' : 'ایجاد حساب کاربری'}
            </button>
          </div>

          <div className="text-xs text-gray-500 text-center">
            با ایجاد حساب کاربری، شما با{' '}
            <a href="#" className="text-primary-600 hover:text-primary-500">
              شرایط استفاده
            </a>{' '}
            و{' '}
            <a href="#" className="text-primary-600 hover:text-primary-500">
              حریم خصوصی
            </a>{' '}
            ما موافقت می‌کنید
          </div>
        </form>
        ) : (
        <form className="mt-8 space-y-6" onSubmit={handleSubmitOTP(onSubmitOTP)}>
          <div>
            <label htmlFor="otpCode" className="block text-sm font-medium text-gray-700">
              کد تأیید
            </label>
            <p className="text-sm text-gray-500 mb-2">
              کد ۶ رقمی ارسال شده به {isOtpOnlyMode ? registrationData?.phoneNumber : registrationData?.email} را وارد کنید
            </p>
            <input
              {...registerOTP('otpCode', {
                required: 'کد تأیید الزامی است',
                pattern: {
                  value: /^[0-9]{6}$/,
                  message: 'کد تأیید باید ۶ رقم باشد'
                }
              })}
              type="text"
              maxLength={6}
              className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm text-center text-2xl tracking-widest"
              placeholder="000000"
            />
            {otpErrors.otpCode && (
              <p className="mt-1 text-sm text-red-600">{otpErrors.otpCode.message}</p>
            )}
          </div>

          <div className="flex space-x-4">
            <button
              type="button"
              onClick={() => setStep('registration')}
              className="flex-1 py-2 px-4 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              بازگشت
            </button>
            <button
              type="submit"
              disabled={isSubmittingOTP}
              className="flex-1 py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmittingOTP ? 'در حال تأیید...' : 'تأیید و ورود'}
            </button>
          </div>
        </form>
        )}
      </div>
    </div>
  );
};

export default RegisterPage; 
